import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";

const todayStr = () => new Date().toISOString().slice(0, 10);
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// ── Recipe URL Importer ──────────────────────────────────────
export async function importRecipeFromUrl(url) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/import-recipe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ url }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to import recipe");
  return data;
}

// ── Auth ─────────────────────────────────────────────────────
export function useAuth() {
  const [session, setSession] = useState(null);
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_ev, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) { setPerson(null); setLoading(false); return; }
    supabase.from("people").select("*").eq("auth_id", session.user.id).single()
      .then(({ data, error }) => {
        if (error) {
          // Try email match and auto-link
          supabase.from("people").select("*").eq("email", session.user.email).single()
            .then(({ data: match }) => {
              if (match) {
                supabase.from("people").update({ auth_id: session.user.id }).eq("id", match.id)
                  .then(() => { setPerson({ ...match, auth_id: session.user.id }); setLoading(false); });
              } else { setPerson(null); setLoading(false); }
            });
        } else { setPerson(data); setLoading(false); }
      });
  }, [session]);

  const signIn = async (email) => {
    const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
    return { error };
  };
  const signOut = async () => { await supabase.auth.signOut(); setPerson(null); };

  return { session, person, loading, signIn, signOut };
}

// ── People ───────────────────────────────────────────────────
export function usePeople() {
  const [people, setPeople] = useState([]);

  const fetchPeople = useCallback(async () => {
    const { data } = await supabase.from("people").select("*").order("name");
    if (data) setPeople(data);
  }, []);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const updatePerson = async (id, updates) => {
    const { error } = await supabase.from("people").update(updates).eq("id", id);
    if (!error) await fetchPeople();
    return { error };
  };

  return { people, fetchPeople, updatePerson };
}

// ── Tasks (with bits, completions, realtime) ─────────────────
export function useTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    const { data } = await supabase.from("tasks")
      .select("*, task_bits(id, label, sort_order), completions(id, person_id, date, note)")
      .order("created_at");
    if (data) {
      setTasks(data.map((t) => ({
        ...t,
        // Normalise field names to camelCase for the UI
        scheduleType: t.schedule_type,
        scheduleDay: t.schedule_day,
        scheduleMonthly: t.schedule_monthly,
        oneOffDate: t.one_off_date,
        startTime: t.start_time,
        doneBy: t.done_by,
        sharedWith: t.shared_with,
        sharedWith2: t.shared_with_2,
        scheduleOffset: t.schedule_offset,
        turnipValue: t.turnip_value,
        bits: (t.task_bits || []).sort((a, b) => a.sort_order - b.sort_order),
        completions_today: (t.completions || []).filter((c) => c.date === todayStr()),
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTasks();
    const subs = ["completions", "tasks", "task_bits"].map((table, i) =>
      supabase.channel(`${table}-ch`).on("postgres_changes",
        { event: "*", schema: "public", table }, () => fetchTasks()).subscribe()
    );
    return () => subs.forEach((s) => supabase.removeChannel(s));
  }, [fetchTasks]);

  const createTask = async (taskData, bits = []) => {
    const { data: task, error } = await supabase.from("tasks").insert({
      title: taskData.title, details: taskData.details, category: taskData.category,
      schedule_type: taskData.scheduleType || "recurring",
      recurrence: taskData.recurrence || null,
      schedule_day: taskData.scheduleDay ?? null,
      schedule_monthly: taskData.scheduleMonthly || null,
      one_off_date: taskData.oneOffDate || null,
      assignee: taskData.assignee,
      shared_with: taskData.sharedWith || null,
      shared_with_2: taskData.sharedWith2 || null,
      start_time: taskData.startTime || null,
      done_by: taskData.doneBy || null,
      schedule_offset: taskData.scheduleOffset || 0,
      turnip_value: taskData.turnipValue ?? 5,
    }).select().single();
    if (error) return { error };
    if (bits.length > 0) {
      await supabase.from("task_bits").insert(bits.map((label, i) => ({ task_id: task.id, label, sort_order: i })));
    }
    await fetchTasks();
    return { data: task };
  };

  const updateTask = async (id, taskData, bits = []) => {
    const { error } = await supabase.from("tasks").update({
      title: taskData.title, details: taskData.details, category: taskData.category,
      schedule_type: taskData.scheduleType || "recurring",
      recurrence: taskData.recurrence || null,
      schedule_day: taskData.scheduleDay ?? null,
      schedule_monthly: taskData.scheduleMonthly || null,
      one_off_date: taskData.oneOffDate || null,
      assignee: taskData.assignee,
      shared_with: taskData.sharedWith || null,
      shared_with_2: taskData.sharedWith2 || null,
      start_time: taskData.startTime || null,
      done_by: taskData.doneBy || null,
      schedule_offset: taskData.scheduleOffset || 0,
      turnip_value: taskData.turnipValue ?? 5,
    }).eq("id", id);
    if (error) return { error };
    await supabase.from("task_bits").delete().eq("task_id", id);
    if (bits.length > 0) {
      await supabase.from("task_bits").insert(bits.map((label, i) => ({ task_id: id, label, sort_order: i })));
    }
    await fetchTasks();
    return { error: null };
  };

  const deleteTask = async (id) => {
    const { error } = await supabase.from("tasks").delete().eq("id", id);
    if (!error) await fetchTasks();
    return { error };
  };

  const toggleComplete = async (taskId, personId, note) => {
    const today = todayStr();
    const { data: rows } = await supabase.from("completions").select("id")
      .eq("task_id", taskId).eq("person_id", personId).eq("date", today);

    if (rows && rows.length > 0) {
      await supabase.from("completions").delete().eq("id", rows[0].id);
      const { data: bits } = await supabase.from("task_bits").select("id").eq("task_id", taskId);
      if (bits?.length) {
        await supabase.from("bit_completions").delete()
          .in("bit_id", bits.map((b) => b.id)).eq("person_id", personId).eq("date", today);
      }
    } else {
      await supabase.from("completions").insert({
        task_id: taskId, person_id: personId, date: today, note: note || null,
      });
    }
    await fetchTasks();
  };

  const toggleBitComplete = async (bitId, personId) => {
    const today = todayStr();
    const existing = await supabase.from("bit_completions").select("id")
      .eq("bit_id", bitId).eq("person_id", personId).eq("date", today).single();
    if (existing.data) {
      await supabase.from("bit_completions").delete().eq("id", existing.data.id);
    } else {
      await supabase.from("bit_completions").insert({ bit_id: bitId, person_id: personId, date: today });
    }
  };

  return { tasks, loading, fetchTasks, createTask, updateTask, deleteTask, toggleComplete, toggleBitComplete };
}

// ── Swaps ────────────────────────────────────────────────────
export function useSwaps() {
  const [swaps, setSwaps] = useState([]);

  const fetchSwaps = useCallback(async () => {
    const { data } = await supabase.from("swap_requests").select("*")
      .eq("date", todayStr()).order("created_at", { ascending: false });
    if (data) setSwaps(data);
  }, []);

  useEffect(() => {
    fetchSwaps();
    const sub = supabase.channel("swaps-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "swap_requests" }, () => fetchSwaps()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchSwaps]);

  const requestSwap = async (taskId, fromPerson, reason, taskTitle) => {
    await supabase.from("swap_requests").insert({
      task_id: taskId, from_person: fromPerson, reason, date: todayStr(),
    });
    await fetchSwaps();
  };

  const reassignSwap = async (swapId, taskId, toPerson, resolvedBy) => {
    await supabase.from("tasks").update({ assignee: toPerson }).eq("id", taskId);
    await supabase.from("swap_requests").update({
      status: "accepted", to_person: toPerson,
      resolved_at: new Date().toISOString(), resolved_by: resolvedBy,
    }).eq("id", swapId);
    await fetchSwaps();
  };

  const dismissSwap = async (swapId, resolvedBy) => {
    await supabase.from("swap_requests").update({
      status: "dismissed", resolved_at: new Date().toISOString(), resolved_by: resolvedBy,
    }).eq("id", swapId);
    await fetchSwaps();
  };

  return { swaps, fetchSwaps, requestSwap, reassignSwap, dismissSwap };
}

// ── Meals ────────────────────────────────────────────────────
export function useMeals() {
  const [meals, setMeals] = useState([]);

  const fetchMeals = useCallback(async () => {
    const { data } = await supabase.from("meal_plan").select("*").order("date");
    if (data) setMeals(data);
  }, []);

  useEffect(() => {
    fetchMeals();
    const sub = supabase.channel("meals-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "meal_plan" }, () => fetchMeals()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchMeals]);

  const saveMeal = async (date, mealData, createdBy) => {
    const payload = {
      title: mealData.title, notes: mealData.notes || null,
      recipe_id: mealData.recipe_id || null,
      cook_time: mealData.cook_time || null, prep_time: mealData.prep_time || null,
      is_prep_ahead: mealData.is_prep_ahead || false, theme: mealData.theme || null,
    };
    const existing = await supabase.from("meal_plan").select("id").eq("date", date).single();
    if (existing.data) {
      await supabase.from("meal_plan").update(payload).eq("date", date);
    } else {
      await supabase.from("meal_plan").insert({ ...payload, date, created_by: createdBy });
    }
    await fetchMeals();
  };

  const deleteMeal = async (date) => {
    await supabase.from("meal_plan").delete().eq("date", date);
    await fetchMeals();
  };

  const copyWeek = async (sourceMonday, targetMonday, createdBy) => {
    // Get all meals from source week (Mon-Sun)
    const sourceEnd = new Date(sourceMonday);
    sourceEnd.setDate(sourceEnd.getDate() + 6);
    const { data: sourceMeals } = await supabase.from("meal_plan").select("*")
      .gte("date", sourceMonday).lte("date", sourceEnd.toISOString().slice(0, 10));
    if (!sourceMeals?.length) return;

    // Clear target week
    const targetEnd = new Date(targetMonday);
    targetEnd.setDate(targetEnd.getDate() + 6);
    await supabase.from("meal_plan").delete()
      .gte("date", targetMonday).lte("date", targetEnd.toISOString().slice(0, 10));

    // Copy with shifted dates
    const sourceMon = new Date(sourceMonday);
    const targetMon = new Date(targetMonday);
    const dayDiff = Math.round((targetMon - sourceMon) / 86400000);

    const copies = sourceMeals.map(m => {
      const newDate = new Date(m.date);
      newDate.setDate(newDate.getDate() + dayDiff);
      return {
        date: newDate.toISOString().slice(0, 10), title: m.title, notes: m.notes,
        recipe_id: m.recipe_id, cook_time: m.cook_time, prep_time: m.prep_time,
        is_prep_ahead: m.is_prep_ahead, theme: m.theme, created_by: createdBy,
      };
    });
    await supabase.from("meal_plan").insert(copies);
    await fetchMeals();
  };

  return { meals, fetchMeals, saveMeal, deleteMeal, copyWeek };
}

// ── Settings ─────────────────────────────────────────────────
export function useSettings() {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    supabase.from("app_settings").select("*").then(({ data }) => {
      if (data) { const obj = {}; data.forEach((s) => (obj[s.key] = s.value)); setSettings(obj); }
    });
  }, []);

  const updateSetting = async (key, value) => {
    await supabase.from("app_settings").update({ value, updated_at: new Date().toISOString() }).eq("key", key);
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return { settings, updateSetting };
}

// ── Categories ───────────────────────────────────────────────
export function useCategories() {
  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from("categories").select("*").order("sort_order");
    if (data) setCategories(data);
  }, []);

  useEffect(() => {
    fetchCategories();
    const sub = supabase.channel("categories-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "categories" }, () => fetchCategories()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchCategories]);

  const createCategory = async (cat) => {
    const slug = cat.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.sort_order || 0)) + 1 : 0;
    const { error } = await supabase.from("categories").insert({
      value: slug, label: cat.label, icon: cat.icon, color: cat.color, bg: cat.bg, sort_order: maxOrder,
    });
    if (!error) await fetchCategories();
    return { error };
  };

  const updateCategory = async (id, updates) => {
    const { error } = await supabase.from("categories").update(updates).eq("id", id);
    if (!error) await fetchCategories();
    return { error };
  };

  const deleteCategory = async (id) => {
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (!error) await fetchCategories();
    return { error };
  };

  return { categories, fetchCategories, createCategory, updateCategory, deleteCategory };
}

// ── Recipes (family recipe bank) ─────────────────────────────
export function useRecipes() {
  const [recipes, setRecipes] = useState([]);

  const fetchRecipes = useCallback(async () => {
    const { data } = await supabase.from("recipes")
      .select("*, recipe_ingredients(id, name, quantity, category, sort_order)")
      .order("title");
    if (data) {
      setRecipes(data.map(r => ({
        ...r,
        ingredients: (r.recipe_ingredients || []).sort((a, b) => a.sort_order - b.sort_order),
      })));
    }
  }, []);

  useEffect(() => {
    fetchRecipes();
    const sub = supabase.channel("recipes-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "recipes" }, () => fetchRecipes()).subscribe();
    const sub2 = supabase.channel("ingredients-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "recipe_ingredients" }, () => fetchRecipes()).subscribe();
    return () => { supabase.removeChannel(sub); supabase.removeChannel(sub2); };
  }, [fetchRecipes]);

  const createRecipe = async (recipe, ingredients = []) => {
    const { data, error } = await supabase.from("recipes").insert({
      title: recipe.title, prep_time: recipe.prep_time || null, cook_time: recipe.cook_time || null,
      servings: recipe.servings || 4, notes: recipe.notes || null, is_favourite: recipe.is_favourite || false,
      tags: recipe.tags || [], created_by: recipe.created_by || null,
      image_url: recipe.image_url || null, source_url: recipe.source_url || null,
    }).select().single();
    if (error) return { error };
    if (ingredients.length > 0) {
      await supabase.from("recipe_ingredients").insert(
        ingredients.map((ing, i) => ({ recipe_id: data.id, name: ing.name, quantity: ing.quantity || null, category: ing.category || "other", sort_order: i }))
      );
    }
    await fetchRecipes();
    return { data };
  };

  const updateRecipe = async (id, recipe, ingredients = []) => {
    await supabase.from("recipes").update({
      title: recipe.title, prep_time: recipe.prep_time || null, cook_time: recipe.cook_time || null,
      servings: recipe.servings || 4, notes: recipe.notes || null, is_favourite: recipe.is_favourite || false,
      tags: recipe.tags || [], image_url: recipe.image_url || null, source_url: recipe.source_url || null,
    }).eq("id", id);
    await supabase.from("recipe_ingredients").delete().eq("recipe_id", id);
    if (ingredients.length > 0) {
      await supabase.from("recipe_ingredients").insert(
        ingredients.map((ing, i) => ({ recipe_id: id, name: ing.name, quantity: ing.quantity || null, category: ing.category || "other", sort_order: i }))
      );
    }
    await fetchRecipes();
  };

  const deleteRecipe = async (id) => {
    await supabase.from("recipes").delete().eq("id", id);
    await fetchRecipes();
  };

  const toggleFavourite = async (id, current) => {
    await supabase.from("recipes").update({ is_favourite: !current }).eq("id", id);
    await fetchRecipes();
  };

  return { recipes, fetchRecipes, createRecipe, updateRecipe, deleteRecipe, toggleFavourite };
}

// ── Shopping List ────────────────────────────────────────────
export function useShoppingList() {
  const [items, setItems] = useState([]);

  const getMonday = () => {
    const d = new Date(); const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(d); mon.setDate(d.getDate() + diff);
    return mon.toISOString().slice(0, 10);
  };

  const fetchList = useCallback(async (weekStart) => {
    const ws = weekStart || getMonday();
    const { data } = await supabase.from("shopping_list").select("*")
      .eq("week_start", ws).order("category").order("sort_order");
    if (data) setItems(data);
  }, []);

  useEffect(() => {
    fetchList();
    const sub = supabase.channel("shopping-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "shopping_list" }, () => fetchList()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchList]);

  const generateFromMeals = async (meals, recipes) => {
    const ws = getMonday();
    // Clear existing auto-generated items for this week
    await supabase.from("shopping_list").delete().eq("week_start", ws).not("from_recipe_id", "is", null);

    // Collect ingredients from all meals that have a recipe
    const ingredientMap = new Map(); // key: lowercase name, value: {name, quantities[], category}
    for (const meal of meals) {
      if (!meal.recipe_id) continue;
      const recipe = recipes.find(r => r.id === meal.recipe_id);
      if (!recipe) continue;
      for (const ing of (recipe.ingredients || [])) {
        const key = ing.name.toLowerCase().trim();
        if (ingredientMap.has(key)) {
          const existing = ingredientMap.get(key);
          if (ing.quantity) existing.quantities.push(ing.quantity);
        } else {
          ingredientMap.set(key, {
            name: ing.name, quantities: ing.quantity ? [ing.quantity] : [],
            category: ing.category || "other", from_recipe_id: recipe.id,
          });
        }
      }
    }

    // Insert deduplicated items
    const toInsert = [];
    let order = 0;
    for (const [, val] of ingredientMap) {
      toInsert.push({
        week_start: ws, name: val.name,
        quantity: val.quantities.length > 1 ? val.quantities.join(" + ") : val.quantities[0] || null,
        category: val.category, from_recipe_id: val.from_recipe_id,
        checked: false, in_pantry: false, sort_order: order++,
      });
    }
    if (toInsert.length > 0) {
      await supabase.from("shopping_list").insert(toInsert);
    }
    await fetchList(ws);
  };

  const addItem = async (name, quantity, category) => {
    const ws = getMonday();
    await supabase.from("shopping_list").insert({
      week_start: ws, name, quantity: quantity || null, category: category || "other",
      checked: false, in_pantry: false, sort_order: items.length,
    });
    await fetchList(ws);
  };

  const toggleChecked = async (id, current) => {
    await supabase.from("shopping_list").update({ checked: !current }).eq("id", id);
    await fetchList();
  };

  const togglePantry = async (id, current) => {
    await supabase.from("shopping_list").update({ in_pantry: !current }).eq("id", id);
    await fetchList();
  };

  const deleteItem = async (id) => {
    await supabase.from("shopping_list").delete().eq("id", id);
    await fetchList();
  };

  const clearChecked = async () => {
    const ws = getMonday();
    const checked = items.filter(i => i.checked).map(i => i.id);
    if (checked.length > 0) {
      await supabase.from("shopping_list").delete().in("id", checked);
    }
    await fetchList(ws);
  };

  return { items, fetchList, generateFromMeals, addItem, toggleChecked, togglePantry, deleteItem, clearChecked, getMonday };
}

// ── Meal Themes ──────────────────────────────────────────────
export function useMealThemes() {
  const [themes, setThemes] = useState([]);

  const fetchThemes = useCallback(async () => {
    const { data } = await supabase.from("meal_themes").select("*").order("sort_order");
    if (data) setThemes(data);
  }, []);

  useEffect(() => { fetchThemes(); }, [fetchThemes]);

  return { themes };
}

// ── Running Low (stock check) ────────────────────────────────
export function useRunningLow() {
  const [items, setItems] = useState([]);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase.from("running_low").select("*").order("created_at", { ascending: false });
    if (data) setItems(data);
  }, []);

  useEffect(() => {
    fetchItems();
    const sub = supabase.channel("running-low-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "running_low" }, () => fetchItems()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchItems]);

  const addItem = async (name, flaggedBy) => {
    await supabase.from("running_low").insert({ name, flagged_by: flaggedBy });
    await fetchItems();
  };

  const toggleChecked = async (id, current) => {
    await supabase.from("running_low").update({ checked: !current }).eq("id", id);
    await fetchItems();
  };

  const deleteItem = async (id) => {
    await supabase.from("running_low").delete().eq("id", id);
    await fetchItems();
  };

  const clearChecked = async () => {
    const checked = items.filter(i => i.checked).map(i => i.id);
    if (checked.length > 0) {
      await supabase.from("running_low").delete().in("id", checked);
    }
    await fetchItems();
  };

  return { items, addItem, toggleChecked, deleteItem, clearChecked };
}

// ── Energy (Octopus Energy) ──────────────────────────────────
export function useEnergy() {
  const [yesterday, setYesterday] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUrl = async (action) => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/octopus-energy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    });
    if (!res.ok) return null;
    return res.json();
  };

  const fetchEnergy = useCallback(async () => {
    setLoading(true);
    try {
      const [yData, wData, lData] = await Promise.all([
        fetchUrl("yesterday"),
        fetchUrl("weekly"),
        fetchUrl("live"),
      ]);
      if (yData) setYesterday(yData);
      if (wData) setWeekly(wData);
      if (lData) setLive(lData);
    } catch (err) {
      console.error("Energy fetch error:", err);
    }
    setLoading(false);
  }, []);

  // Refresh live data every 30 seconds
  const fetchLive = useCallback(async () => {
    try {
      const lData = await fetchUrl("live");
      if (lData) setLive(lData);
    } catch (err) { /* silently fail */ }
  }, []);

  useEffect(() => { fetchEnergy(); }, [fetchEnergy]);

  useEffect(() => {
    const interval = setInterval(fetchLive, 30000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  return { yesterday, weekly, live, loading, refresh: fetchEnergy };
}

// ── Bin Collections ──────────────────────────────────────────
export function useBinCollections() {
  const [collections, setCollections] = useState([]);

  const fetchCollections = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase.from("bin_collections").select("*")
      .gte("put_out_date", today).order("put_out_date").limit(4);
    if (data) setCollections(data);
  }, []);

  useEffect(() => { fetchCollections(); }, [fetchCollections]);

  return { collections };
}

// ── DIY Projects ─────────────────────────────────────────────
export function useDIY() {
  const [projects, setProjects] = useState([]);

  const fetchProjects = useCallback(async () => {
    const { data } = await supabase.from("diy_projects")
      .select("*, diy_subtasks(id, label, is_complete, sort_order)")
      .order("is_complete").order("sort_order");
    if (data) {
      setProjects(data.map(p => ({
        ...p,
        subtasks: (p.diy_subtasks || []).sort((a, b) => a.sort_order - b.sort_order),
      })));
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    const sub1 = supabase.channel("diy-projects-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "diy_projects" }, () => fetchProjects()).subscribe();
    const sub2 = supabase.channel("diy-subtasks-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "diy_subtasks" }, () => fetchProjects()).subscribe();
    return () => { supabase.removeChannel(sub1); supabase.removeChannel(sub2); };
  }, [fetchProjects]);

  const createProject = async (project, subtasks = []) => {
    const { data, error } = await supabase.from("diy_projects").insert({
      title: project.title, description: project.description || null,
      priority: project.priority || "medium", area: project.area || "other",
      estimated_time: project.estimated_time || null,
      sort_order: projects.length,
    }).select().single();
    if (error) return { error };
    if (subtasks.length > 0) {
      await supabase.from("diy_subtasks").insert(
        subtasks.map((label, i) => ({ project_id: data.id, label, sort_order: i }))
      );
    }
    await fetchProjects();
    return { data };
  };

  const updateProject = async (id, project, subtasks = []) => {
    await supabase.from("diy_projects").update({
      title: project.title, description: project.description || null,
      priority: project.priority || "medium", area: project.area || "other",
      estimated_time: project.estimated_time || null,
    }).eq("id", id);
    await supabase.from("diy_subtasks").delete().eq("project_id", id);
    if (subtasks.length > 0) {
      await supabase.from("diy_subtasks").insert(
        subtasks.map((s, i) => ({
          project_id: id, label: typeof s === "string" ? s : s.label,
          is_complete: typeof s === "string" ? false : s.is_complete || false, sort_order: i,
        }))
      );
    }
    await fetchProjects();
  };

  const deleteProject = async (id) => {
    await supabase.from("diy_projects").delete().eq("id", id);
    await fetchProjects();
  };

  const toggleSubtask = async (subtaskId, current) => {
    await supabase.from("diy_subtasks").update({ is_complete: !current }).eq("id", subtaskId);
    await fetchProjects();
  };

  const toggleProject = async (id, current) => {
    await supabase.from("diy_projects").update({
      is_complete: !current,
      completed_at: !current ? new Date().toISOString() : null,
    }).eq("id", id);
    await fetchProjects();
  };

  return { projects, createProject, updateProject, deleteProject, toggleSubtask, toggleProject };
}

// ── Hearts (observer encouragement) ──────────────────────────
export function useHearts() {
  const [hearts, setHearts] = useState([]);

  const fetchHearts = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase.from("hearts").select("*")
      .eq("date", today);
    if (data) setHearts(data);
  }, []);

  useEffect(() => {
    fetchHearts();
    const sub = supabase.channel("hearts-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "hearts" }, () => fetchHearts()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchHearts]);

  const sendHeart = async (fromPerson, toPerson) => {
    await supabase.from("hearts").insert({ from_person: fromPerson, to_person: toPerson });
    await fetchHearts();
  };

  return { hearts, sendHeart };
}

// ── Turnip Economy ───────────────────────────────────────────
export function useTurnips() {
  const [ledger, setLedger] = useState([]);

  const fetchLedger = useCallback(async () => {
    const { data } = await supabase.from("turnip_ledger").select("*").order("created_at", { ascending: false });
    if (data) setLedger(data);
  }, []);

  useEffect(() => {
    fetchLedger();
    const sub = supabase.channel("turnips-ch").on("postgres_changes",
      { event: "*", schema: "public", table: "turnip_ledger" }, () => fetchLedger()).subscribe();
    return () => supabase.removeChannel(sub);
  }, [fetchLedger]);

  const earnTurnips = async (personId, taskId, amount) => {
    await supabase.from("turnip_ledger").insert({
      person_id: personId,
      task_id: taskId,
      amount,
      reason: "task_complete",
    });
    await fetchLedger();
  };

  const revokeTurnips = async (personId, taskId) => {
    const today = new Date().toISOString().slice(0, 10);
    const { data: rows } = await supabase.from("turnip_ledger").select("id")
      .eq("person_id", personId).eq("task_id", taskId).eq("date", today);
    if (rows && rows.length > 0) {
      await supabase.from("turnip_ledger").delete().eq("id", rows[0].id);
      await fetchLedger();
    }
  };

  // Get total turnips per person
  const getTotals = () => {
    const totals = {};
    ledger.forEach(e => {
      totals[e.person_id] = (totals[e.person_id] || 0) + e.amount;
    });
    return totals;
  };

  // Get today's earnings per person
  const getTodayEarnings = () => {
    const today = new Date().toISOString().slice(0, 10);
    const totals = {};
    ledger.filter(e => e.date === today).forEach(e => {
      totals[e.person_id] = (totals[e.person_id] || 0) + e.amount;
    });
    return totals;
  };

  // Get this week's earnings per person
  const getWeekEarnings = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    startOfWeek.setHours(0, 0, 0, 0);
    const weekStart = startOfWeek.toISOString().slice(0, 10);
    const totals = {};
    ledger.filter(e => e.date >= weekStart).forEach(e => {
      totals[e.person_id] = (totals[e.person_id] || 0) + e.amount;
    });
    return totals;
  };

  return { ledger, earnTurnips, revokeTurnips, getTotals, getTodayEarnings, getWeekEarnings };
}